// @flow

import { Animation } from './animation';
import { BaseRenderer } from './base.renderer';
import { ChartModel } from './chart.model';
import { ANIMATION_STEPS, SCALE_COLOR, SCALE_NUMBER, SCALE_OFFSET } from './constants';
import { getChartMaxValue, getScaleStep } from './utils/chart.utils';

export class ScaleRenderer extends BaseRenderer {
    step: number;
    pxStep: number;
    chartMax: number;
    offset: number;

    constructor (chart: ChartModel, width: number, height: number) {
        super(chart, width, height);
        this.progressAnimation = false;
        const max = this.chart.getMaxValue();
        this.step = getScaleStep(max, SCALE_NUMBER);
        this.chartMax = this.step * SCALE_NUMBER;
        this.pxStep = (height - SCALE_OFFSET) / SCALE_NUMBER;
        this.offset = SCALE_OFFSET;
        this.render()
    }

    render () {
        this.clear();
        const { width, height } = this.canvas;
        this.context.lineWidth = 1;
        this.context.strokeStyle = SCALE_COLOR;
        if (this.chart.yScaled && this.chart.columns.filter(col => !col._disabled).length > 1) {
            this.chart.columns.forEach((col, index) => {
                const max = col.getMaxColumnValue(this.chart.selectStart, this.chart.selectEnd);
                const step = getScaleStep(max, SCALE_NUMBER);
                this.context.fillStyle = col.color;
                this.context.beginPath();
                this.context.moveTo(0, height - (SCALE_OFFSET + 0.5));
                this.context.lineTo(width, height - (SCALE_OFFSET + 0.5));
                this.context.fillText('0', index * (width - 30), height - (SCALE_OFFSET + 10));
                for (let i = 1; i < SCALE_NUMBER + 1; i++) {
                    const top = Math.floor(height - (this.offset + i * this.pxStep));
                    this.context.moveTo(0, top - 0.5);
                    this.context.lineTo(width, top - 0.5);
                    this.context.fillText(((step * i) || 0).toString(10), index * (width - 30), top - 10);
                }
                this.context.stroke();
            });
        } else {
            this.context.fillStyle = SCALE_COLOR;
            this.context.beginPath();
            this.context.moveTo(0, height - (SCALE_OFFSET + 0.5));
            this.context.lineTo(width, height - (SCALE_OFFSET + 0.5));
            this.context.fillText('0', 0, height - (SCALE_OFFSET + 10));
            for (let i = 1; i < SCALE_NUMBER + 1; i++) {
                const top = Math.floor(height - (this.offset + i * this.pxStep));
                this.context.moveTo(0, top - 0.5);
                this.context.lineTo(width, top - 0.5);
                this.context.fillText(((this.step * i) || 0).toString(10), 0, top - 10);
            }
            this.context.stroke();
        }
        const x0 = this.chart.selectStart;
        const xl = this.chart.selectEnd;
        const step = width / (xl - x0);
        const k = Math.round(60 / step);

        for (let i = x0; i < xl; i++) {
            const x = (i - x0) * step;
            if (i % k === 0) {
                this.context.fillStyle = SCALE_COLOR;
                this.context.fillText(this.chart.row.dates[i], x, height - 30);
            }
        }
    }

    update (changes: any) {
        if (changes.chartMax !== undefined && changes.chartMax !== this.chartMax) {
            this.progressAnimation = true;
            this.step = Math.floor(changes.chartMax / SCALE_NUMBER);
            const offset = changes.chartMax > this.chartMax ? 0 : SCALE_OFFSET * 2;
            this.animations.chartMax = { animation: new Animation(this.pxStep, offset, 1) };
            this.chartMax = changes.chartMax;
        }
        if (changes.move !== undefined) {
            this.progressAnimation = true;
            const max = this.chart.getMaxSelectionValue();
            const chartMax = getChartMaxValue(max, SCALE_NUMBER);
            this.update({ chartMax });
        }
    }

    draw () {
        if (this.progressAnimation) {
            if (this.animations.chartMax) {
                const { animation } = this.animations.chartMax;
                this.offset = animation.getValue();
                if (animation.finished()) {
                    this.offset = SCALE_OFFSET;
                    this.progressAnimation = false;
                    delete this.animations.chartMax;
                }
            } else {
                this.progressAnimation = false;
            }
            this.render();
        }
        return this.canvas;
    }

    clear () {
        const { width, height } = this.canvas;
        this.context.clearRect(0, 0, width, height);
    }
}
